import { DOCUMENT, inject, Injectable } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { AuthTokenType, AzureLoginResult, AzureOAuthOptions } from '@libs/shared/models';
import { BehaviorSubject, catchError, combineLatest, filter, from, map, Observable, switchMap, take, throwError } from 'rxjs';
import { OAuthConfigService } from '../../oauth-config.service';
import {
  getLlDirectoryUserAuthority,
  getUserAuthority,
  LL_DIRECTORY_USER_MSAL_GUARD_CONFIG,
  LL_DIRECTORY_USER_MSAL_SERVICE,
} from './providers';

// this is based on the reference implementation by ms
// https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/samples/msal-angular-v3-samples/angular-b2c-sample-app
@Injectable()
export class AzureOAuthWebService {
  private readonly configService: OAuthConfigService = inject(OAuthConfigService);
  private readonly msalBroadcastService: MsalBroadcastService = inject(MsalBroadcastService);

  private readonly llDirectoryUserMsalConfig: AzureOAuthOptions;
  private readonly llDirectoryUserMsal: MsalService = inject(LL_DIRECTORY_USER_MSAL_SERVICE);
  private readonly llDirectoryUserMsalGuardConfig: MsalGuardConfiguration = inject(LL_DIRECTORY_USER_MSAL_GUARD_CONFIG);
  private readonly llDirectoryUserMsalInitialized$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private readonly userMsalConfig: AzureOAuthOptions;
  private readonly userMsal: MsalService = inject(MsalService);
  private readonly userMsalGuardConfig: MsalGuardConfiguration = (inject(MSAL_GUARD_CONFIG) as unknown as MsalGuardConfiguration);
  private readonly userMsalInitialized$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private readonly document: Document = inject(DOCUMENT);

  public constructor() {
    this.llDirectoryUserMsalConfig = this.configService.getLlDirectoryUserAzureOAuthOptions();
    this.userMsalConfig = this.configService.getUserAzureOAuthOptions();

    // we need to wait for both MSAL instances to be initialized before we for
    // example refresh the token, otherwise we might get an error that the account is not found
    // This usually happens when opening reopening the page.
    // Usually we would listen via the broadcast service for these events
    // but because we have 2 MSAL instances that
    // share the same broadcast service, we cannot distinguish between the 2 instances.
    // As far as I know we cannot provide the broadcast service for both instances separately
    this.userMsal.instance.addEventCallback(() => {
      this.userMsalInitialized$.next(true);
    }, [EventType.INITIALIZE_END]);

    this.llDirectoryUserMsal.instance.addEventCallback(() => {
      this.llDirectoryUserMsalInitialized$.next(true);
    }, [EventType.INITIALIZE_END]);
  }

  public msalIdle$(): Observable<InteractionStatus> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
    );
  }

  public userMsalReady$(): Observable<boolean> {
    return this.userMsalInitialized$.pipe(filter(ready => ready));
  }

  public llDirectoryUserMsalReady$(): Observable<boolean> {
    return this.llDirectoryUserMsalInitialized$.pipe(filter((ready) => ready));
  }

  public msalReadyAndIdle$(): Observable<true> {
    return combineLatest([this.userMsalReady$(), this.llDirectoryUserMsalReady$(), this.msalIdle$()]).pipe(map(() => true));
  }

  public loginUser(): Observable<void> {
    if (this.userMsalGuardConfig.authRequest) {
      return this.userMsal.loginRedirect({ ...this.userMsalGuardConfig.authRequest } as RedirectRequest);
    } else {
      return this.userMsal.loginRedirect();
    }
  }

  /**
   * Starts the B2C sign-up policy via MSAL. MSAL generates PKCE and state,
   * the redirect result is handled by the AzureOAuthWebResultHandlerService like a regular login.
   */
  public registerUser(): Observable<void> {
    const registerRequest: RedirectRequest = {
      ...this.userMsalGuardConfig.authRequest,
      authority: getUserAuthority(this.userMsalConfig, this.userMsalConfig.signUpPolicy),
      scopes: this.userMsalConfig.scope,
    };

    return this.userMsal.loginRedirect(registerRequest);
  }

  public loginLlDirectoryUser(): Observable<void> {
    if (this.llDirectoryUserMsalGuardConfig.authRequest) {
      return this.llDirectoryUserMsal.loginRedirect({ ...this.llDirectoryUserMsalGuardConfig.authRequest } as RedirectRequest);
    } else {
      return this.llDirectoryUserMsal.loginRedirect();
    }
  }

  public refreshToken(): Observable<AzureLoginResult> {
    return this.msalReadyAndIdle$().pipe(take(1), switchMap(() => {
      const currentAuthTokenType: AuthTokenType | undefined = this.getCurrentAuthTokenType();

      if (currentAuthTokenType === 'llDirectoryUser') {
        return this.refreshLlDirectoryUserToken();
      } else {
        return this.refreshUserToken();
      }
    }));
  }

  public logout(): Observable<void> {
    const currentAuthTokenType: AuthTokenType | undefined = this.getCurrentAuthTokenType();

    if (currentAuthTokenType === 'llDirectoryUser') {
      return this.logoutLlDirectoryUser();
    } else {
      return this.logoutUser();
    }
  }

  private refreshUserToken(): Observable<AzureLoginResult> {
    const account: AccountInfo | undefined = this.getActiveUserMsalAccount();

    const signUpSignInFlowRequest: SilentRequest = {
      authority: getUserAuthority(this.userMsalConfig, this.getPolicyFromAccount(this.userMsalConfig, account)),
      redirectUri: this.userMsalConfig.redirectUri,
      scopes: this.userMsalConfig.scope,
      account: account,
    };

    if (!account) {
      return this.acquireUserTokenRedirect(signUpSignInFlowRequest);
    }

    return this.userMsal.acquireTokenSilent(signUpSignInFlowRequest).pipe(map((result) => {
      return {
        accessToken: result.accessToken,
        expiresOn: result.expiresOn?.getTime().toString() ?? undefined,
        idToken: result.idToken,
        accountId: result.account.nativeAccountId,
        type: 'user',
      } satisfies AzureLoginResult;
    }), catchError((e: unknown) => {
      console.error(e);

      if (e instanceof InteractionRequiredAuthError) {
        return this.acquireUserTokenRedirect(signUpSignInFlowRequest);
      }

      return throwError(() => e);
    }));
  }

  /** * Casts `Observable<void>` to `Observable<AzureLoginResult>`
   * because * `acquireTokenRedirect` redirects the browser immediately —
   * the page is * unloaded before the observable ever emits,
   * so no actual value is produced * and the cast is safe. */
  private acquireUserTokenRedirect(signUpSignInFlowRequest: SilentRequest): Observable<AzureLoginResult> {
    return this.msalIdle$().pipe(take(1), switchMap(() => this.userMsal.acquireTokenRedirect({
        ...signUpSignInFlowRequest,
        redirectStartPage: this.document.location.href,
      }).pipe(catchError((e: unknown) => {
        console.error('acquireTokenRedirect failed', e);

        return throwError(() => e);
      })),
    )) as unknown as Observable<AzureLoginResult>;
  }

  private refreshLlDirectoryUserToken(): Observable<AzureLoginResult> {
    const account: AccountInfo | undefined = this.getActiveLlDirectoryMsalAccount();

    const signUpSignInFlowRequest: SilentRequest = {
      authority: getLlDirectoryUserAuthority(this.llDirectoryUserMsalConfig, this.getPolicyFromAccount(this.llDirectoryUserMsalConfig, account)),
      redirectUri: this.llDirectoryUserMsalConfig.redirectUri,
      scopes: this.llDirectoryUserMsalConfig.scope,
      account: account,
    };

    return this.llDirectoryUserMsal.acquireTokenSilent(signUpSignInFlowRequest).pipe(map((result) => {
      return {
        accessToken: result.accessToken,
        expiresOn: result.expiresOn?.getTime().toString() ?? undefined,
        idToken: result.idToken,
        accountId: result.account.nativeAccountId,
        type: 'llDirectoryUser',
      } satisfies AzureLoginResult;
    }), catchError((e: unknown) => {
      if (e instanceof InteractionRequiredAuthError) {
        return this.acquireLlDirectoryUserTokenRedirect(signUpSignInFlowRequest);
      }

      return throwError(() => e);
    }));
  }

  /** * Casts `Observable<void>` to `Observable<AzureLoginResult>`
   * because * `acquireTokenRedirect` redirects the browser immediately —
   * the page is * unloaded before the observable ever emits,
   * so no actual value is produced * and the cast is safe. */
  private acquireLlDirectoryUserTokenRedirect(signUpSignInFlowRequest: SilentRequest): Observable<AzureLoginResult> {
    return this.msalIdle$().pipe(take(1), switchMap(() => this.llDirectoryUserMsal.acquireTokenRedirect({
      ...signUpSignInFlowRequest,
      redirectStartPage: this.document.location.href,
    }).pipe(catchError((e: unknown) => {
      console.error('acquireTokenRedirect failed', e);

      return throwError(() => e);
    })))) as unknown as Observable<AzureLoginResult>;
  }

  private getCurrentAuthTokenType(): AuthTokenType | undefined {
    const currentUserAccount: AccountInfo | undefined = this.getActiveUserMsalAccount();
    const currentLlDirectoryUserAccount: AccountInfo | undefined = this.getActiveLlDirectoryMsalAccount();

    if (currentUserAccount) {
      return 'user';
    }

    if (currentLlDirectoryUserAccount) {
      return 'llDirectoryUser';
    }

    return undefined;
  }

  private logoutUser(): Observable<void> {
    return this.msalIdle$().pipe(take(1), switchMap(() => this.userMsal.logoutRedirect()));
  }

  private logoutLlDirectoryUser(): Observable<void> {
    this.llDirectoryUserMsal.instance.setActiveAccount(null);

    return from(this.llDirectoryUserMsal.instance.clearCache());
  }

  private getActiveUserMsalAccount(): AccountInfo | undefined {
    const account: AccountInfo | undefined = this.userMsal.instance.getActiveAccount() ?? this.userMsal.instance.getAllAccounts()[0] ?? undefined;

    if (account?.environment !== this.userMsalConfig.authorityHostname) {
      return undefined;
    }

    return account;
  }

  private getActiveLlDirectoryMsalAccount(): AccountInfo | undefined {
    const account: AccountInfo | undefined = this.llDirectoryUserMsal.instance.getActiveAccount() ?? this.llDirectoryUserMsal.instance.getAllAccounts()[0] ?? undefined;

    if (account?.environment !== this.llDirectoryUserMsalConfig.authorityHostname) {
      return undefined;
    }

    return account;
  }

  /**
   * Resolves the active B2C policy name from the account's ID token claims.
   * Checks `tfp` first (Trust Framework Policy), then falls back to `acr`
   * (Authentication Context Class Reference). Reading the policy from the token
   * is necessary because the user may have previously reset their password,
   * which places them on a different policy than the default sign-in one.
   * If neither claim is present, returns the configured default sign-in policy.
   *
   * @param msalConfig - The MSAL config whose `signInPolicy` is used as the fallback.
   * @param account - The currently active MSAL account, or `undefined` if none.
   * @returns The resolved policy name.
   */
  private getPolicyFromAccount(msalConfig: AzureOAuthOptions, account: AccountInfo | undefined): string {
    const claims: Record<string, string> | undefined = account?.idTokenClaims as Record<string, string> | undefined;
    const policy: string | undefined = claims?.['tfp'] ?? claims?.['acr'];

    if (!policy) {
      return msalConfig.signInPolicy;
    }

    return policy;
  }
}
